// Vendor
import * as React from 'react';

// Shared Components
import CenteredSpinner from 'isee/ui/components/centered-spinner';

// Components
import ErrorDialog from './components/error-dialog';

// Utils
import {compose} from 'isee/utils/compose';
import {retryable} from 'isee/utils/retryable';
import {timeoutable} from 'isee/utils/timeoutable';

// Constants
const MAX_TRY_COUNT = 3;
const TIMEOUT_DELAY_MS = 10000;

// Types
type ComponentType<Props> = React.ComponentClass<Props> | React.SFC<Props>;

interface ComponentModule<Props> {
  default: ComponentType<Props>;
}

interface State<Props> {
  component: ComponentType<Props> | null;
  failed: boolean;
}

type ImportComponent<Props> = () => Promise<ComponentModule<Props>>;

export const asyncComponent = <Props = {}>(
  importComponent: ImportComponent<Props>
): React.ComponentClass<Props> => {
  const fetchComponent = compose(
    retryable(MAX_TRY_COUNT),
    timeoutable(TIMEOUT_DELAY_MS)
  )(importComponent);

  class AsyncComponent extends React.Component<Props, State<Props>> {
    public static displayName = '';

    public readonly state: Readonly<State<Props>> = {
      component: null,
      failed: false
    };

    public async componentDidMount() {
      try {
        const {default: component} = await fetchComponent();
        this.setState(() => ({component}));
      } catch {
        this.setState(() => ({failed: true}));
      }
    }

    public render() {
      const {component: Component, failed} = this.state;

      if (failed) {
        return React.createElement(ErrorDialog);
      }

      if (Component) {
        return React.createElement(Component, this.props);
      }

      return React.createElement(CenteredSpinner, {delayMS: 500, padding: 50});
    }
  }

  return AsyncComponent;
};

export default asyncComponent;



// WEBPACK FOOTER //
// ./src/ui/components/async-component/async-component.ts